import React, { useState } from 'react';
import { Button } from '@mui/material';
import { IoDownloadOutline } from 'react-icons/io5';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import PhiDisclaimerFooter from '@brands/Components/PhiDisclaimerFooter/PhiDisclaimerFooter';
import InCallSection from './InCallSection';
import CaseDetailsCard from './CaseDetailsCard';
import AppointmentChecklistCard from './AppointmentChecklistCard';
import AttachmentsCard from './AttachmentsCard';
import OralHealthCoachingAttachmentsCard from './OralHealthCoachingAttachmentsCard';
import SoapNotesCard from './SoapNotesCard';
import FulfillmentApprovalCard from './FulfillmentApprovalCard';
import ProviderAttestationCard from './ProviderAttestationCard';
import FollowUpSection from '@brands/Dashboard/ConsultationPage/CollapsibleSections/FollowUpSection/FollowUpSection';
import { config } from '@brands/config/config';
import SubmitActions from './SubmitActions';
import styles from '../styles.module.scss';
import { CaseType, ICase, Status } from '@brands/services/cases/types/ICase';
import { setIsVisible, setVCCaseId } from '@brands/store/slices/videoCallCredentialsSlice';
import OralHealthCoachingReportCard from './OralHealthCoachingReportCard';
import NoteModal from '../CollapsibleSections/CaseNotesSection/modal/AddNotesModal';
import AddGoalModal from '../Modals/AddGoalModal/AddGoalModal';
import AddConsultationSummaryModal from '../Modals/AddConsultationSummaryModal/AddConsultationSummaryModal';
import { addOneReportInternalNote } from '@brands/services/oneReport/addOneReportInternalNote';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { getCasePatientRx } from '@brands/services/cases/getCasePatientRx';
import { CasePayload } from '@brands/services/cases/createNewCase';
import { addOralHealthCoachingReportObject } from '@brands/services/oralHealthCoachingReport/addOralHealthCoachingReportObject';
import { ObjectType } from '@brands/services/careCoordinationReport/types/types';
import {
  CreateOralHealthCoachingReportEducationParams,
  CreateOralHealthCoachingReportGoalParams,
  CreateOralHealthCoachingReportInternalNoteParams,
  Goal,
  OralHealthCoachingReport,
} from '@brands/services/oralHealthCoachingReport/types/types';
import { updateOralHealthCoachingReportObject } from '@brands/services/oralHealthCoachingReport/updateOralHealthCoachingReportObject';
import { deleteOralHealthCoachingReportObject } from '@brands/services/oralHealthCoachingReport/deleteOralHealthCoachingReportObject';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { toast } from 'react-toastify';
import { FieldErrors, UseFormSetValue } from 'react-hook-form';
import { OralHealthReportSubmit } from '@brands/Dashboard/ConsultationPage/assets/types';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import CareCoordinationReportCard from './CareCoordinationReportCard';
import ReportTextModal from '../Modals/ReportTextModal/ReportTextModal';
import { addCareCoordinationReportObject } from '@brands/services/careCoordinationReport/addCareCoordinationReportObject';
import { updateCareCoordinationObject } from '@brands/services/careCoordinationReport/updateCareCoordinationReportObject';
import {
  CareCoordinationReportNotes,
  CareCoordinationReportBenefitsNavigation,
} from '@brands/services/careCoordinationReport/types/types';
import { useParams } from 'react-router-dom';
import {
  canDownloadConsultationReport,
  getConsultationReportDownloadLabel,
  isCompletedReportCase,
} from '../utils/reportDownload';

type Props = {
  thisCase: ICase;
  userInfo: any;
  timeZone: string;
  shouldHideInCallSection: boolean;
  showInCallSection: boolean;
  inCallParticipants: any[];
  dispatch: any;
  consultationForm: any;
  setConsultationForm: any;
  allowProviderUpdate: boolean;
  allowAttachmentUpdate: boolean;
  handleSubmit: any;
  onSubmit: any;
  onInvalid: any;
  setValue: UseFormSetValue<OralHealthReportSubmit>;
  errors: FieldErrors<OralHealthReportSubmit>;
  register: any;
  getValues: any;
  trigger: any;
  control: any;
  addRequiredField: boolean;
  setAddRequiredField: any;
  changeCaseType: (selectedCaseType: string) => Promise<void>;
  changeCaseStatus: (status: 'completed' | 'canceled', reason?: string, reasonOther?: string) => Promise<void>;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  editCase: (case_id: number, body: CasePayload) => Promise<ICase | null>;
  rescheduleCase: (case_id: number, video_conference_date: string) => Promise<ICase | null>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  accept: (checked: boolean) => Promise<void>;
  images: any[];
  setImages: any;
  setEditImageNote: any;
  setRemoveAttachment: any;
  setRemoveImage: any;
  updateSelectedImages: (updatedReport: any) => void;
  checkCollapsibleHeaderColor: any;
  addPhoto: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDownloadReport: () => Promise<void>;
  setEditingFulfillmentApproval: React.Dispatch<React.SetStateAction<boolean | null>>;
  journeyPaused?: boolean;
  journeyAtLimit?: boolean;
};

const CaseContentPanel = ({
  thisCase,
  userInfo,
  timeZone,
  shouldHideInCallSection,
  showInCallSection,
  inCallParticipants,
  dispatch,
  consultationForm,
  setConsultationForm,
  allowProviderUpdate,
  allowAttachmentUpdate,
  handleSubmit,
  onSubmit,
  onInvalid,
  setCase,
  setValue,
  errors,
  register,
  getValues,
  trigger,
  control,
  addRequiredField,
  setAddRequiredField,
  changeCaseType,
  changeCaseStatus,
  editCase,
  rescheduleCase,
  loading,
  setLoading,
  accept,
  images,
  setImages,
  setEditImageNote,
  setRemoveAttachment,
  setRemoveImage,
  updateSelectedImages,
  checkCollapsibleHeaderColor,
  addPhoto,
  onDownloadReport,
  setEditingFulfillmentApproval,
  journeyPaused = false,
  journeyAtLimit = false,
}: Props): JSX.Element => {
  const [isInternalNotesModalOpen, setIsInternalNotesModalOpen] = useState(false);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isConsultationSummaryModalOpen, setIsConsultationSummaryModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const routeCaseId = useParams().caseId as string;
  const [isCCInternalNotesModalOpen, setIsCCInternalNotesModalOpen] = useState(false);
  const [isCCNoteModalOpen, setIsCCNoteModalOpen] = useState(false);
  const [isBenefitsNavigationModalOpen, setIsBenefitsNavigationModalOpen] = useState(false);
  const [editingCCNote, setEditingCCNote] = useState<CareCoordinationReportNotes | null>(null);
  const [editingBenefit, setEditingBenefit] = useState<CareCoordinationReportBenefitsNavigation | null>(null);

  const openInternalNotesModal = (): void => {
    setConsultationForm((prev) => ({
      ...prev,
      caseNotes: '',
    }));
    setIsInternalNotesModalOpen(true);
  };
  const closeInternalNotesModal = (): void => setIsInternalNotesModalOpen(false);

  const openGoalModal = (): void => setIsGoalModalOpen(true);
  const closeGoalModal = (): void => setIsGoalModalOpen(false);

  const openConsultationSummaryModal = (): void => setIsConsultationSummaryModalOpen(true);
  const closeConsultationSummaryModal = (): void => setIsConsultationSummaryModalOpen(false);
  async function handleSaveInternalNote(noteText: string): Promise<void> {
    const reportId = thisCase.oral_health_coaching_report?.id;
    if (!reportId) {
      // Reading `.id` off a missing report threw inside this un-awaited
      // handler, so Save looked dead: no request, no error, nothing.
      // Older Oral Health Coaching cases can still lack a report.
      toast.error('This case has no Oral Health Coaching report yet, so notes cannot be saved.', {
        className: 'fs-unmask',
      });
      return;
    }

    try {
      await addOralHealthCoachingReportObject(
        String(reportId),
        'internal_note',
        {
          ['note']: noteText,
        } as CreateOralHealthCoachingReportInternalNoteParams,
        userInfo.role.name,
      );

      const currentCase = await getOneCase(Number(thisCase.id), undefined, userInfo.role.name);

      setCase((prev) => ({
        ...prev,
        oral_health_coaching_report: currentCase.oral_health_coaching_report,
        updated_at: currentCase.updated_at,
      }));

      setConsultationForm({
        ...consultationForm,
        oralHealthCoachingInternalNote: currentCase.oral_health_coaching_report?.internal_notes,
      });
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  }

  const saveObjectType = async (note: string, objectType: ObjectType): Promise<void> => {
    await addCareCoordinationReportObject(thisCase.care_coordination_report?.id.toString() as string, objectType, {
      [objectType === 'internal_note' ? 'note' : objectType]: note,
    } as Record<typeof objectType, string>);
    const currentCase = await getOneCase(Number(routeCaseId));
    setCase({
      ...thisCase,
      care_coordination_report: currentCase.care_coordination_report,
      updated_at: currentCase.updated_at,
    });

    setConsultationForm({ ...consultationForm, [objectType]: '' });
    setValue(objectType as 'benefits_navigation' | 'note' | 'internal_note', '');
  };

  const handleSaveCCInternalNote = async (note: string): Promise<void> => {
    await saveObjectType(note, 'internal_note');
    closeCCInternalNotesModal();
  };

  const handleSaveCCNote = async (note: string): Promise<void> => {
    const reportId = thisCase.care_coordination_report?.id?.toString();
    if (!reportId) return;

    if (editingCCNote) {
      await updateCareCoordinationObject(reportId, 'note', editingCCNote.id.toString(), { note });
      const currentCase = await getOneCase(Number(routeCaseId));
      setCase((prev) => ({ ...prev, care_coordination_report: currentCase.care_coordination_report }));
    } else {
      await saveObjectType(note, 'note');
    }
    setEditingCCNote(null);
    closeCCNoteModal();
  };

  const handleSaveBenefitsNavigation = async (note: string): Promise<void> => {
    const reportId = thisCase.care_coordination_report?.id?.toString();
    if (!reportId) return;

    if (editingBenefit) {
      await updateCareCoordinationObject(reportId, 'benefits_navigation', editingBenefit.id.toString(), {
        benefits_navigation: note,
      });
      const currentCase = await getOneCase(Number(routeCaseId));
      setCase((prev) => ({ ...prev, care_coordination_report: currentCase.care_coordination_report }));
    } else {
      await saveObjectType(note, 'benefits_navigation');
    }
    setEditingBenefit(null);
    closeBenefitsNavigationModal();
  };

  const openCCInternalNotesModal = (): void => setIsCCInternalNotesModalOpen(true);
  const closeCCInternalNotesModal = (): void => setIsCCInternalNotesModalOpen(false);

  const openCCNoteModal = (note?: CareCoordinationReportNotes | null): void => {
    setEditingCCNote(note ?? null);
    setConsultationForm((prev: any) => ({ ...prev, note: note?.note ?? '' }));
    setIsCCNoteModalOpen(true);
  };
  const closeCCNoteModal = (): void => {
    setEditingCCNote(null);
    setIsCCNoteModalOpen(false);
  };

  const openBenefitsNavigationModal = (benefit?: CareCoordinationReportBenefitsNavigation | null): void => {
    setEditingBenefit(benefit ?? null);
    setConsultationForm((prev: any) => ({ ...prev, benefits_navigation: benefit?.benefits_navigation ?? '' }));
    setIsBenefitsNavigationModalOpen(true);
  };
  const closeBenefitsNavigationModal = (): void => {
    setEditingBenefit(null);
    setIsBenefitsNavigationModalOpen(false);
  };

  const handleSaveGoal = async (goalText: string, educations: { url: string; title: string }[]): Promise<void> => {
    const reportId = thisCase.oral_health_coaching_report?.id;

    if (!reportId || !goalText.trim()) {
      return;
    }

    try {
      let updatedReport: OralHealthCoachingReport | undefined;
      let goalId: number | undefined;

      const editingGoalId = editingGoal && typeof editingGoal === 'object' ? Number(editingGoal.id) : undefined;

      if (editingGoalId) {
        updatedReport = await updateOralHealthCoachingReportObject(
          reportId.toString(),
          'goal',
          editingGoalId.toString(),
          {
            goal_text: goalText.trim(),
          } as CreateOralHealthCoachingReportGoalParams,
        );

        goalId = editingGoalId;
      } else {
        updatedReport = await addOralHealthCoachingReportObject(reportId.toString(), 'goal', {
          goal_text: goalText.trim(),
        } as CreateOralHealthCoachingReportGoalParams);

        goalId = updatedReport.care_plan?.goals?.[updatedReport.care_plan.goals.length - 1]?.id;
      }

      if (!goalId) {
        setCase((prev) => ({
          ...prev,
          oral_health_coaching_report: updatedReport as OralHealthCoachingReport,
        }));
        setEditingGoal(null);
        setIsGoalModalOpen(false);
        return;
      }

      const existingGoal =
        editingGoalId && updatedReport?.care_plan?.goals
          ? updatedReport.care_plan.goals.find((goal) => Number(goal.id) === Number(goalId))
          : undefined;

      const existingEducations = existingGoal?.educations || [];

      for (let index = 0; index < educations.length; index++) {
        const education = educations[index];

        if (!education.title.trim() && !education.url.trim()) {
          continue;
        }

        const existingEducation = existingEducations[index];

        if (existingEducation?.id) {
          updatedReport = await updateOralHealthCoachingReportObject(
            reportId.toString(),
            'education',
            existingEducation.id.toString(),
            {
              goal_id: goalId,
              title: education.title.trim(),
              url: education.url.trim(),
            } as CreateOralHealthCoachingReportEducationParams,
          );
        } else {
          updatedReport = await addOralHealthCoachingReportObject(reportId.toString(), 'education', {
            goal_id: goalId,
            title: education.title.trim(),
            url: education.url.trim(),
          } as CreateOralHealthCoachingReportEducationParams);
        }
      }

      if (editingGoalId && existingEducations.length > educations.length) {
        const educationsToDelete = existingEducations.slice(educations.length);

        for (const education of educationsToDelete) {
          if (!education?.id) continue;

          updatedReport = await deleteOralHealthCoachingReportObject(
            reportId.toString(),
            education.id.toString(),
            'education',
          );
        }
      }

      if (updatedReport) {
        setCase((prev) => ({
          ...prev,
          oral_health_coaching_report: updatedReport as OralHealthCoachingReport,
        }));

        setConsultationForm({
          ...consultationForm,
          care_plan: updatedReport.care_plan,
        });
      }

      setEditingGoal(null);
      setIsGoalModalOpen(false);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  const handleSaveConsultationSummary = async (value: string): Promise<void> => {
    const reportId = thisCase.oral_health_coaching_report?.id;
    const carePlanId = thisCase.oral_health_coaching_report?.care_plan?.id;

    if (!reportId || !carePlanId) {
      return;
    }

    try {
      const updatedReport = await updateOralHealthCoachingReportObject(
        reportId.toString(),
        'care_plan',
        carePlanId.toString(),
        {
          consultation_summary: value,
        },
      );

      if (updatedReport) {
        setCase((prev) => ({
          ...prev,
          oral_health_coaching_report: updatedReport as OralHealthCoachingReport,
        }));
        setConsultationForm({
          ...consultationForm,
          care_plan: updatedReport.care_plan,
        });
      }

      setIsConsultationSummaryModalOpen(false);
    } catch (error) {
      displayErrorDetails(error);
    }
  };

  const handleDeleteConsultationSummary = async (): Promise<void> => {
    const reportId = thisCase.oral_health_coaching_report?.id;
    const carePlanId = thisCase.oral_health_coaching_report?.care_plan?.id;

    if (!reportId || !carePlanId) {
      return;
    }

    try {
      const updatedReport = await updateOralHealthCoachingReportObject(
        reportId.toString(),
        'care_plan',
        carePlanId.toString(),
        {
          consultation_summary: '',
        },
      );

      setCase((prev) => ({
        ...prev,
        oral_health_coaching_report: updatedReport as OralHealthCoachingReport,
      }));
    } catch (error) {
      displayErrorDetails(error);
    }
  };
  function handleDeleteCareCoordinationNote(noteId: number): Promise<void> {
    throw new Error('Function not implemented.');
  }
  function handleDeleteBenefitsNavigation(noteId: number): Promise<void> {
    throw new Error('Function not implemented.');
  }

  const openEPrescribePlatform = async (): Promise<void> => {
    try {
      const caseId = parseInt(routeCaseId, 10);
      window.open('', 'formpopup', 'width=1500,height=1000,resizeable,scrollbars');
      const icorePayload = await getCasePatientRx(caseId, userInfo.role.name);

      const loginform = document.getElementById('icoreForm');
      const loginInput = document.getElementById('icoreFormData');

      if (loginform && loginInput) {
        (loginform as HTMLFormElement).target = 'formpopup';
        (loginInput as HTMLInputElement).value = JSON.stringify(icorePayload);
        (loginform as HTMLFormElement).submit();
      }
    } catch (error) {
      displayErrorDetails(`Error accessing ePrescribe platform: ${error}`);
    }
  };

  /**
   * Oral Health Coaching cases have no `one_report`, which is what
   * `AttachmentsCard` hangs every upload off — its Add button silently
   * did nothing here. Route those cases to the report's own attachments.
   */
  const renderOralHealthCoachingAttachmentsCard = (): JSX.Element => (
    <OralHealthCoachingAttachmentsCard
      thisCase={thisCase}
      setCase={setCase}
      userInfo={userInfo}
      allowAttachmentUpdate={allowAttachmentUpdate}
    />
  );

  const renderAttachmentsCard = (): JSX.Element => (
    <AttachmentsCard
      consultationForm={consultationForm}
      setConsultationForm={setConsultationForm}
      thisCase={thisCase}
      setCase={setCase}
      userInfo={userInfo}
      setValue={setValue}
      errors={errors}
      images={images}
      setImages={setImages}
      setEditImageNote={setEditImageNote}
      setRemoveAttachment={setRemoveAttachment}
      setRemoveImage={setRemoveImage}
      updateSelectedImages={updateSelectedImages}
      allowAttachmentUpdate={allowAttachmentUpdate}
      addPhoto={addPhoto}
      checkCollapsibleHeaderColor={checkCollapsibleHeaderColor}
    />
  );

  const renderReportSection = () => {
    switch (thisCase.type) {
      case CaseType.care_coordination:
        return (
          <>
            <CareCoordinationReportCard
              thisCase={thisCase}
              setCase={setCase}
              userInfo={userInfo}
              openInternalNotesModal={openCCInternalNotesModal}
              openCareCoordinationNotesModal={openCCNoteModal}
              openBenefitsNavigationModal={openBenefitsNavigationModal}
            />

            {renderAttachmentsCard()}

            <ReportTextModal
              open={isCCInternalNotesModalOpen}
              onClose={closeCCInternalNotesModal}
              onSave={handleSaveCCInternalNote}
              value={consultationForm.internal_note || ''}
              setValue={(value) =>
                setConsultationForm((prev) => ({
                  ...prev,
                  internal_note: value,
                }))
              }
              title="Add internal case notes"
              submitText="Add internal case notes"
              placeholder="Add internal case notes here"
              warningText="Note: internal notes are not shared with the patient"
            />

            <ReportTextModal
              open={isCCNoteModalOpen}
              onClose={closeCCNoteModal}
              onSave={handleSaveCCNote}
              value={consultationForm.note || ''}
              setValue={(value) =>
                setConsultationForm((prev) => ({
                  ...prev,
                  note: value,
                }))
              }
              title={editingCCNote ? 'Edit Care Coordination notes' : 'Add Care Coordination notes'}
              submitText={editingCCNote ? 'Save Care Coordination notes' : 'Add Care Coordination notes'}
              placeholder="Add Care Coordination notes here"
            />

            <ReportTextModal
              open={isBenefitsNavigationModalOpen}
              onClose={closeBenefitsNavigationModal}
              onSave={handleSaveBenefitsNavigation}
              value={consultationForm.benefits_navigation || ''}
              setValue={(value) =>
                setConsultationForm((prev) => ({
                  ...prev,
                  benefits_navigation: value,
                }))
              }
              title={editingBenefit ? 'Edit Benefits Navigation' : 'Add Benefits Navigation'}
              submitText={editingBenefit ? 'Save Benefits Navigation' : 'Add Benefits Navigation'}
              placeholder="Add Benefits Navigation here"
            />

            {config.features.multiStage && (
              <FollowUpSection
                consultationForm={consultationForm}
                setConsultationForm={setConsultationForm}
                thisCase={thisCase}
                setCase={setCase}
                userInfo={userInfo}
                errors={errors}
                journeyPaused={journeyPaused}
                journeyAtLimit={journeyAtLimit}
              />
            )}
          </>
        );
      case CaseType.oral_health_coaching:
        return (
          <>
            <OralHealthCoachingReportCard
              openInternalNotesModal={openInternalNotesModal}
              openGoalModal={openGoalModal}
              openConsultationSummaryModal={openConsultationSummaryModal}
              thisCase={thisCase}
              setCase={setCase}
              userInfo={userInfo}
              setEditingGoal={setEditingGoal}
              onDeleteConsultationSummary={handleDeleteConsultationSummary}
              errors={errors}
            />

            {renderOralHealthCoachingAttachmentsCard()}

            <NoteModal
              open={isInternalNotesModalOpen}
              onClose={closeInternalNotesModal}
              onSave={handleSaveInternalNote}
              consultationForm={consultationForm}
              setConsultationForm={setConsultationForm}
              thisCase={thisCase}
              type="INTERNAL_CASE_NOTE"
            />

            <AddGoalModal
              isOpen={isGoalModalOpen}
              onClose={closeGoalModal}
              onSave={handleSaveGoal}
              initialGoalText={editingGoal?.goal_text || ''}
              initialEducations={
                editingGoal?.educations?.map((education) => ({
                  url: education.url || '',
                  title: education.title || '',
                })) || []
              }
            />

            <AddConsultationSummaryModal
              isOpen={isConsultationSummaryModalOpen}
              onClose={closeConsultationSummaryModal}
              onSave={handleSaveConsultationSummary}
              initialValue={thisCase.oral_health_coaching_report?.care_plan?.consultation_summary || ''}
            />

            {config.features.multiStage && (
              <FollowUpSection
                consultationForm={consultationForm}
                setConsultationForm={setConsultationForm}
                thisCase={thisCase}
                setCase={setCase}
                userInfo={userInfo}
                errors={errors}
                journeyPaused={journeyPaused}
                journeyAtLimit={journeyAtLimit}
              />
            )}
          </>
        );

      case CaseType.video_call_scheduled:
      case CaseType.phone_call_scheduled:
      case CaseType.second_opinion:
      case CaseType.one_report:
      case CaseType.clinical_enablement:
        return (
          <>
            <AppointmentChecklistCard
              thisCase={thisCase}
              checked={consultationForm?.is_terms_accepted === true}
              disabled={!allowProviderUpdate}
              onChange={async (checked) => {
                setConsultationForm((prev: any) => ({
                  ...prev,
                  verifyingInformation: checked,
                  is_terms_accepted: checked,
                }));
                await accept(checked);
              }}
              errors={errors}
            />

            {config.features.multiStage && (
              <FulfillmentApprovalCard
                thisCase={thisCase}
                setCase={setCase}
                setEditingFulfillmentApproval={setEditingFulfillmentApproval}
                disabled={!allowProviderUpdate}
                errors={errors}
              />
            )}

            {renderAttachmentsCard()}

            <SoapNotesCard
              consultationForm={consultationForm}
              setConsultationForm={setConsultationForm}
              thisCase={thisCase}
              setCase={setCase}
              userInfo={userInfo}
              register={register}
              setValue={setValue}
              getValues={getValues}
              trigger={trigger}
              errors={errors}
              control={control}
              addRequiredField={addRequiredField}
              setAddRequiredField={setAddRequiredField}
              journeyPaused={journeyPaused}
              journeyAtLimit={journeyAtLimit}
            />

            <ProviderAttestationCard thisCase={thisCase} timeZone={timeZone} />
          </>
        );

      default:
        return renderAttachmentsCard();
    }
  };

  const isAssignedProvider =
    userInfo.role.name === UserRoleName.Provider && Number(thisCase.provider_id) === Number(userInfo.id);

  const canDownloadReport = canDownloadConsultationReport(thisCase, userInfo.role.name);
  // ePrescribe only needs the case completed, so it stays even when the case was closed without a finished report.
  const canAccessEPrescribe = isAssignedProvider && isCompletedReportCase(thisCase);

  return (
    <>
      {(canDownloadReport || canAccessEPrescribe) && (
        <div className={styles.consultationPageRightSideSubmit}>
          {canDownloadReport && (
            <Button
              className={styles.consultationPageRightSideSubmitBtn}
              style={{ flexDirection: 'row' }}
              variant="contained"
              startIcon={<IoDownloadOutline />}
              onClick={onDownloadReport}
            >
              {getConsultationReportDownloadLabel(thisCase.type)}
            </Button>
          )}

          {canAccessEPrescribe && (
            <Button
              className={styles.consultationPageRightSideSubmitBtnOutlined}
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              onClick={openEPrescribePlatform}
            >
              Access ePrescribe platform
            </Button>
          )}
        </div>
      )}

      {!shouldHideInCallSection &&
        showInCallSection &&
        (thisCase.type === CaseType.video_call_scheduled || thisCase.type === CaseType.oral_health_coaching) &&
        // Hide the whole In Call card (including the "Join video call"
        // button) once the case is completed or canceled — matches the
        // gate CareAdvisorLayout and ConsultationHeader already apply.
        // See DO-3960. `checkCollapsibleHeaderColor` is provided via
        // props by the parent (reusing rather than re-importing avoids
        // shadowing the prop name).
        checkCollapsibleHeaderColor(thisCase || '', userInfo, 'JoinWaitingRoom') !== 'readOnly' && (
          <InCallSection
            participants={inCallParticipants}
            onJoin={() => {
              dispatch(setIsVisible(true));
              dispatch(setVCCaseId(thisCase.id.toString()));
            }}
          />
        )}

      <CaseDetailsCard
        thisCase={thisCase}
        userInfo={userInfo}
        timeZone={timeZone}
        changeCaseType={changeCaseType}
        changeCaseStatus={changeCaseStatus}
        setCase={setCase}
        editCase={editCase}
        rescheduleCase={rescheduleCase}
        loading={loading}
        setLoading={setLoading}
      />

      <form
        className={styles.consultationPageRightSideForm}
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {userInfo.role.name !== UserRoleName.Admin
          ? renderReportSection()
          : thisCase.type === CaseType.oral_health_coaching
            ? renderOralHealthCoachingAttachmentsCard()
            : renderAttachmentsCard()}

        {((userInfo.id === thisCase.care_advisor_id &&
          thisCase?.status === 'in_progress_care_advisor' &&
          userInfo.role.name === UserRoleName.CareAdvisor) ||
          (userInfo.id === thisCase.provider_id &&
            thisCase?.status === 'in_progress_provider' &&
            userInfo.role.name === UserRoleName.Provider)) && (
          <SubmitActions visible={allowProviderUpdate} caseType={thisCase.type} />
        )}
      </form>

      <PhiDisclaimerFooter />
    </>
  );
};

export default CaseContentPanel;
